import { Fragment } from 'react/jsx-runtime';
import { OrderProduct, OrderType } from '../../../../../clients/quipli-service/order/order';
import { Order } from '../../../../../clients/quipli-service/order/order';
import { OrderInputItem } from '../../../../../clients/quipli-service/order/order-calculation';
import { Typography } from '../../../../../components/typography/typography';
import { ProductItem } from '../product-item/product-item';
import {
  CustomProductFeeRequest,
  CustomProductLineItem,
  FinancialSummary,
  ProductFeeLineItem,
  ProductLineItem,
} from '../../../../../clients/quipli-service/financials/financials';
import { ProductPrice, RentalProductPrice } from '../../../../../clients/quipli-service/pricing/pricing';
import { Location } from '../../../../../clients/quipli-service/location/location';
import { Configuration } from '../../../../../clients/quipli-service/configuration/configuration';
import { safeRound } from '../../../../../util/util';

interface Props {
  order: Order;
  isEditMode: boolean;
  existingRentalProducts: OrderProduct[];
  existingSaleProducts: OrderProduct[];
  newRentalProducts: OrderInputItem[];
  newSaleProducts: OrderInputItem[];
  location: Location;
  setEditsValid: (valid: boolean) => void;
  isEditable: boolean;
  showRates: boolean;
  isCustomOrderRentalRatesEnabled: boolean;
  onlyShowStoreHoursOnDateTimePicker: boolean;
  productPrices: Map<string, ProductPrice[]>;
  summary: FinancialSummary | undefined;
  configurations: Configuration[];
  deliveryDescriptions: { productId: number; text: string }[];
  taxCode: { id?: string; name?: string; taxCodeSource?: string } | undefined;
  showTaxInclusive: boolean;
  updateProduct: (input: OrderInputItem) => void;
  deleteOrderProduct: (id: string) => void;
  refresh: () => Promise<void>;
  orderInputs: OrderInputItem[];
  setCustomProductFees: (value: React.SetStateAction<CustomProductFeeRequest[]>) => void;
  newProducts: OrderInputItem[];
}

export const ProductList = ({
  order,
  isEditMode,
  existingRentalProducts,
  existingSaleProducts,
  newRentalProducts,
  newSaleProducts,
  location,
  setEditsValid,
  isEditable,
  showRates,
  isCustomOrderRentalRatesEnabled,
  onlyShowStoreHoursOnDateTimePicker,
  productPrices,
  summary,
  configurations,
  deliveryDescriptions,
  taxCode,
  showTaxInclusive,
  updateProduct,
  deleteOrderProduct,
  refresh,
  orderInputs,
  setCustomProductFees,
  newProducts,
}: Props) => {
  const renderOrderProduct = (op?: OrderProduct, oi?: OrderInputItem) => {
    const exists = !!op;
    const id = (op?.id || oi?.id)!;
    const productId = (op?.product.id || oi?.productId)!;
    const type = (op?.type || oi?.type)!;

    return (
      <Fragment key={id}>
        <ProductItem
          order={order}
          product={op}
          location={location}
          setEditsValid={setEditsValid}
          isEditing={isEditable}
          isShowingPrices={showRates}
          isCustomOrderRentalRatesEnabled={isCustomOrderRentalRatesEnabled}
          onlyShowStoreHoursOnDateTimePicker={onlyShowStoreHoursOnDateTimePicker}
          rentalPrices={
            type === OrderType.Rental && op
              ? (productPrices.get(`${op.id}`) as RentalProductPrice[] | undefined)
              : undefined
          }
          inputItem={exists ? orderInputs.find((input) => input.id === `${op.id}`) : oi}
          financialItem={
            !exists
              ? (summary?.lineItems.find(
                  (line) => (line.type === 'Product' || line.type === 'CustomProduct') && line.id === oi?.id,
                ) as ProductLineItem | CustomProductLineItem | undefined)
              : undefined
          }
          financialFees={
            (summary?.lineItems.filter((line) => line.type === 'ProductFee' && line.productLineItemId === `${id}`) ||
              []) as ProductFeeLineItem[]
          }
          onChange={updateProduct}
          onDelete={deleteOrderProduct}
          onChangeFee={(fee, newPrice) => {
            setCustomProductFees((prev) => [
              ...prev.filter(
                (custom) => !(custom.feeId === fee.productFeeId && custom.productLineItemId === fee.productLineItemId),
              ),
              {
                feeId: fee.productFeeId,
                productId: fee.productId,
                productLineItemId: fee.productLineItemId,
                amount: safeRound(newPrice * 100),
              } as CustomProductFeeRequest,
            ]);
          }}
          onResetFee={(fee) => {
            setCustomProductFees((prev) =>
              prev.filter(
                (custom) => !(custom.feeId === fee.productFeeId && custom.productLineItemId === fee.productLineItemId),
              ),
            );
          }}
          refresh={refresh}
          saleSerialTrackingEnabled={
            configurations.find((c) => c.variable === 'CONF_SALE_SERIAL_TRACKING')?.value === '1'
          }
          deliveryDescription={deliveryDescriptions.find((d) => d.productId === productId)}
          taxCodeId={taxCode?.id}
          isTaxInclusive={showTaxInclusive}
        />
      </Fragment>
    );
  };

  // Filter out products that have been deleted from orderInputs
  const filterDeletedProducts = (products: OrderProduct[]) => {
    return products.filter((op) => orderInputs.find((input) => input.id === `${op.id}`));
  };

  const filteredExistingRentalProducts = filterDeletedProducts(existingRentalProducts);
  const filteredExistingSaleProducts = filterDeletedProducts(existingSaleProducts);

  return (
    <>
      {order.isCycleBilled ? (
        <>
          {(filteredExistingRentalProducts.length > 0 || (isEditMode && newRentalProducts.length > 0)) && (
            <>
              <div className="min-w-full flex gap-2">
                <Typography type={'text-sm'} weight={'semi-bold'} className="text-[#1D252C]">
                  Rental Items
                </Typography>
              </div>

              {filteredExistingRentalProducts.map((op) => renderOrderProduct(op))}
              {isEditMode && newRentalProducts.map((oi) => renderOrderProduct(undefined, oi))}
            </>
          )}

          {(filteredExistingSaleProducts.length > 0 || (isEditMode && newSaleProducts.length > 0)) && (
            <>
              <div className="min-w-full flex gap-2">
                <Typography type={'text-sm'} weight={'semi-bold'} className="text-[#1D252C]">
                  Sale / Misc. Items
                </Typography>
              </div>

              {filteredExistingSaleProducts.map((op) => renderOrderProduct(op))}
              {isEditMode && newSaleProducts.map((oi) => renderOrderProduct(undefined, oi))}
            </>
          )}
        </>
      ) : (
        <>
          {filterDeletedProducts(order.products).map((op) => renderOrderProduct(op))}
          {isEditMode && newProducts.map((oi) => renderOrderProduct(undefined, oi))}
        </>
      )}
    </>
  );
};
